import { Alert, Button, Modal, SelectField, TextField } from "@templeos/ui";
import { useState, type FormEvent, type ReactNode } from "react";
import { errorText } from "../lib/api.js";

export interface SupervisorApproval { username: string; password: string; reason: string }

interface Props {
  open: boolean;
  title: string;
  children?: ReactNode;
  reasons: readonly string[];         // reason codes offered to the cashier
  confirmLabel: string;
  danger?: boolean;
  onClose: () => void;
  onApprove: (a: SupervisorApproval) => Promise<void>;
}

/**
 * A supervisor approves an action at the cashier's screen with their own credentials (void, duplicate print,
 * gate override). The server verifies the credentials and records the approval in the audit trail.
 */
export function SupervisorModal({ open, title, children, reasons, confirmLabel, danger, onClose, onApprove }: Props) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const close = () => {
    setReason(""); setNote(""); setUsername(""); setPassword(""); setError(null);
    onClose();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const full = reason === "Other" ? note.trim() : [reason, note.trim()].filter(Boolean).join(" - ");
    if (!reason) return setError("Choose a reason.");
    if (full.length < 5) return setError("Describe the reason (at least 5 characters).");
    if (!username.trim() || !password) return setError("The supervisor must enter their username and password.");
    setBusy(true);
    setError(null);
    try {
      await onApprove({ username: username.trim(), password, reason: full });
      close();
    } catch (err) {
      setPassword("");
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} title={title} onClose={close}
      footer={<><Button onClick={close}>Cancel</Button><Button type="submit" form="tos-supervisor-form" variant={danger ? "danger" : "primary"} loading={busy}>{confirmLabel}</Button></>}>
      <form id="tos-supervisor-form" className="tos-stack" onSubmit={submit} noValidate>
        {children}
        <SelectField label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Choose a reason"
          options={[...reasons, "Other"].map((r) => ({ value: r, label: r }))} />
        <TextField label={reason === "Other" ? "Reason details" : "Note"} required={reason === "Other"} value={note} maxLength={180} onChange={(e) => setNote(e.target.value)} />
        <div className="tos-form-grid">
          <TextField label="Supervisor username" required autoComplete="off" value={username} onChange={(e) => setUsername(e.target.value)} />
          <TextField label="Supervisor password" required type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && <Alert tone="danger">{error}</Alert>}
      </form>
    </Modal>
  );
}
